const WORDPRESS_API_BASE = 'https://webdev.lacnic.net/wp-json/wp/v2';

class WordPressApiService {
    constructor() {
        this.baseUrl = WORDPRESS_API_BASE;
    }

    /**
     * Mapea el idioma de la aplicación al parámetro de WordPress
     * @param {string} lang - Idioma de la aplicación ('es', 'en', 'pt')
     * @returns {string|null} - Parámetro de idioma para WordPress
     */
    getLangParam(lang) {
        const langMap = {
            'es': null,        // Español es el idioma por defecto
            'en': 'en',        // Inglés
            'pt': 'pt-br'       // Portugués brasileño
        };
        
        return langMap[lang] || null;
    }

    /**
     * Obtiene el slug base en español para un slug de idioma específico
     * @param {string} slug - Slug actual
     * @param {string} lang - Idioma del slug
     * @returns {string} - Slug base en español
     */
    getBaseSlug(slug, lang) {
        // Mapeo de slugs conocidos de otros idiomas a sus equivalentes en español
        const slugMap = {
            // Inglés -> Español
             
            'a-study-of-ddos-attacks': 'investigacion-sobre-ataques-ddos',
            'implementation-of-rfc8950-for-ipv6-only-transition-ipv4aas-support-in-mikrotik-networks': 'implementacion-del-rfc8950-para-la-transicion-a-ipv6-only-soporte-de-ipv4aas-en-redes-mikrotik',
            'global-and-regional-evolution-of-rpki-adoption': 'evolucion-global-y-regional-de-la-adopcion-de-rpki',
            
            // Portugués -> Español
            'pesquisa-sobre-ataques-de-ddos': 'investigacion-sobre-ataques-ddos',
            'implementacao-do-rfc8950-para-a-transicao-para-so-ipv6-suporte-do-ipv4aas-em-redes-mikrotik': 'implementacion-del-rfc8950-para-la-transicion-a-ipv6-only-soporte-de-ipv4aas-en-redes-mikrotik'
        };

        // Si tenemos un mapeo específico, usarlo
        if (slugMap[slug]) {
            return slugMap[slug];
        }

        // Si no hay mapeo y no es español, intentar usar el slug tal como está
        // (puede ser que el slug ya sea en español)
        return slug;
    }

    /**
     * Obtiene todos los informes técnicos
     * @param {Object} params - Parámetros de consulta
     * @param {string} lang - Idioma ('es', 'en', 'pt')
     * @returns {Promise<Array>}
     */
    async getInformesTecnicos(params = {}, lang = 'es') {
        try {
            const queryParams = new URLSearchParams({
                per_page: 100,
                status: 'publish',
                ...params
            });

            // Agregar parámetro de idioma si es necesario
            const langParam = this.getLangParam(lang);
            if (langParam) {
                queryParams.append('lang', langParam);
            }

            const response = await fetch(`${this.baseUrl}/informes-tecnicos?${queryParams}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return this.transformInformesData(data);
        } catch (error) {
            console.error(`Error fetching informes técnicos (lang: ${lang}):`, error);
            throw error;
        }
    }

    /**
     * Obtiene un informe técnico específico por ID
     * @param {number} id - ID del informe
     * @returns {Promise<Object>}
     */
    async getInformeTecnicoById(id) {
        try {
            const response = await fetch(`${this.baseUrl}/informes-tecnicos/${id}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return this.transformInformeData(data);
        } catch (error) {
            console.error(`Error fetching informe técnico ${id}:`, error);
            throw error;
        }
    }

    /**
     * Obtiene un informe técnico por slug
     * @param {string} slug - Slug del informe
     * @param {string} lang - Idioma ('es', 'en', 'pt')
     * @returns {Promise<Object>}
     */
    async getInformeTecnicoBySlug(slug, lang = 'es') {
        try {
            // Mapeo de idiomas a parámetros de WordPress
            const langParam = this.getLangParam(lang);
            
            // Construir URL con parámetro de idioma si es necesario
            let url = `${this.baseUrl}/informes-tecnicos?slug=${slug}`;
            if (langParam) {
                url += `&lang=${langParam}`;
            }
            
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            let data = await response.json();
            
            // Si no encontramos datos y no estamos en español, intentar fallback al español
            if (data.length === 0 && lang !== 'es') {
                console.log(`🔄 No se encontró contenido en ${lang}, intentando fallback al español para slug: ${slug}`);
                
                // Obtener el slug base en español
                const baseSlug = this.getBaseSlug(slug, lang);
                console.log(`🔄 Intentando con slug base en español: ${baseSlug}`);
                
                // Intentar con el slug base en español
                const fallbackUrl = `${this.baseUrl}/informes-tecnicos?slug=${baseSlug}`;
                const fallbackResponse = await fetch(fallbackUrl);
                
                if (fallbackResponse.ok) {
                    const fallbackData = await fallbackResponse.json();
                    if (fallbackData.length > 0) {
                        console.log(`✅ Fallback exitoso: usando contenido en español para slug base: ${baseSlug}`);
                        return this.transformInformeData(fallbackData[0]);
                    }
                }
                
                console.log(`⚠️ Fallback falló: no se encontró contenido ni en ${lang} ni en español`);
            }
            
            return data.length > 0 ? this.transformInformeData(data[0]) : null;
        } catch (error) {
            console.error(`Error fetching informe técnico by slug ${slug} (lang: ${lang}):`, error);
            throw error;
        }
    }

    /**
     * Transforma los datos de WordPress al formato esperado por la aplicación
     * @param {Array} wpData - Datos de WordPress
     * @returns {Array}
     */
    transformInformesData(wpData) {
        return wpData.map(informe => this.transformInformeData(informe));
    }

    /**
     * Transforma un informe individual de WordPress al formato esperado
     * @param {Object} wpInforme - Informe de WordPress
     * @returns {Object}
     */
    transformInformeData(wpInforme) {
        const acf = wpInforme.acf || {};
        const informacion = acf.informacion_informe || {};
        const secciones = acf.informe_tecnico || [];

        return {
            id: wpInforme.id,
            slug: wpInforme.slug,
            title: wpInforme.title?.rendered || '',
            content: wpInforme.content?.rendered || '',
            date: wpInforme.date,
            modified: wpInforme.modified,
            link: wpInforme.link,
            status: wpInforme.status,
            
            // Información del informe
            informacion: {
                autor: informacion.autor || '',
                coordinacion: informacion.coordinacion_y_revision || '',
                edicion: informacion.edicion || '',
                area: informacion.area || '',
                fecha: informacion.fecha || '',
                proyecto: informacion.proyecto || '',
                version: informacion.version || '',
                archivo: informacion.archivo || false
            },

            // Secciones del informe
            secciones: secciones.map(seccion => ({
                titulo: seccion.titulo_de_la_seccion || '',
                contenido: seccion.contenido_de_la_seccion || '',
                subsecciones: seccion.subsecciones || []
            })),

            // Metadatos adicionales
            wpml_translations: wpInforme.wpml_translations || [],
            featured_media: wpInforme.featured_media,
            class_list: wpInforme.class_list || []
        };
    }

    /**
     * Obtiene informes por categoría/área
     * @param {string} area - Área temática
     * @returns {Promise<Array>}
     */
    async getInformesByArea(area) {
        try {
            const informes = await this.getInformesTecnicos();
            return informes.filter(informe => {
                const informeArea = informe.informacion.area.toLowerCase();
                const searchArea = area.toLowerCase();
                
                // Buscar coincidencias más flexibles
                return informeArea.includes(searchArea) || 
                       informeArea.includes('interconexion') ||
                       informeArea.includes('infraestructura') ||
                       informeArea.includes('bgp') ||
                       informeArea.includes('conectividad');
            });
        } catch (error) {
            console.error(`Error fetching informes by area ${area}:`, error);
            throw error;
        }
    }

    /**
     * Busca informes por término
     * @param {string} searchTerm - Término de búsqueda
     * @param {string} lang - Idioma ('es', 'en', 'pt')
     * @returns {Promise<Array>}
     */
    async searchInformes(searchTerm, lang = 'es') {
        try {
            let url = `${this.baseUrl}/informes-tecnicos?search=${encodeURIComponent(searchTerm)}`;
            
            // Agregar parámetro de idioma si es necesario
            const langParam = this.getLangParam(lang);
            if (langParam) {
                url += `&lang=${langParam}`;
            }
            
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return this.transformInformesData(data);
        } catch (error) {
            console.error(`Error searching informes (lang: ${lang}):`, error);
            throw error;
        }
    }
}

// Crear instancia singleton
const wordpressApi = new WordPressApiService();

export default wordpressApi;
